// ============================================================================
// Common Types
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export interface DataResponse<T> {
  data: T;
  message?: string;
  operation_id?: string;
}

export type VisibilityType = "private" | "team" | "public";

// ============================================================================
// Attachment upload error detail shapes (mirrors backend structured errors)
// ============================================================================

export interface UnsupportedFileTypeDetail {
  error_code: "unsupported_file_type";
  mime_type: string;
  filename: string | null;
  allowed_image_types: string[];
  allowed_document_types: string[];
}

export interface FileTooLargeDetail {
  error_code: "file_too_large";
  file_kind: "image" | "document";
  size: number;
  max_size: number;
}

export interface TooManyFilesDetail {
  error_code: "too_many_files";
  count: number;
  max: number;
}

export type AttachmentErrorDetail = UnsupportedFileTypeDetail | FileTooLargeDetail | TooManyFilesDetail;

/** Type guard — checks that a value is a fully-shaped structured attachment error detail. */
export function isAttachmentErrorDetail(value: unknown): value is AttachmentErrorDetail {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  switch (v.error_code) {
    case "unsupported_file_type":
      return (
        typeof v.mime_type === "string" &&
        Array.isArray(v.allowed_image_types) &&
        Array.isArray(v.allowed_document_types)
      );
    case "file_too_large":
      return typeof v.file_kind === "string" && typeof v.size === "number" && typeof v.max_size === "number";
    case "too_many_files":
      return typeof v.count === "number" && typeof v.max === "number";
    default:
      return false;
  }
}
